import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import VoiceTrackIcon from '../../../static/icons/ic_voicetrack_16.svg';
import * as Actions from '../../actions';
import GridItem from '../../components/lib/GridItem/GridItem';
import LastUpdated from '../../components/lib/LastUpdated/LastUpdated';
import * as Constants from '../../constants';
import { messages } from '../../constants/Collection';
import { GridConstants } from '../../constants/Grid';
import { PandoraTypes } from '../../constants/SourceTypes';
import { getInDomain } from '../../domains/externalUtils';
import { getArt, getHero } from '../../lib/ArtUtils';
import { isMegastarModesEnabled } from '../../lib/configuration/TunerVarUtils';
import { mapCuratorProps } from '../../lib/CuratorSourcesUtils';
import { isAPSPandoraStationEnabled } from '../../lib/ListenerUtils';
import { getProfilePath, getStationDetailsPathForStationId } from '../../lib/RouteUtils';
import { getSourceCardMenuItems } from '../../lib/SourceCardUtils';
import Icon from '../Icon';

import { messages as stationGridItemMessages } from './constants';

// StationGridItem is a more-or-less direct conversion of MyStationsGridItem into
// the new T3 grid item, for use in My Music.

const voiceTrackIcon = <Icon glyph={VoiceTrackIcon} />;

class StationGridItem extends React.Component {
  static propTypes = {
    addStationToShuffleList: PropTypes.func.isRequired,
    blankItem: PropTypes.bool,
    canShuffle: PropTypes.bool,
    componentName: PropTypes.string,
    createdBy: PropTypes.string,
    curatorName: PropTypes.string,
    curatorUrlPath: PropTypes.string,
    dateCollected: PropTypes.string,
    dominantColor: PropTypes.string,
    editStationUrl: PropTypes.string,
    hasTakeoverModes: PropTypes.bool,
    id: PropTypes.string,
    imgUrl: PropTypes.string,
    intl: PropTypes.object,
    isAdvertiserStation: PropTypes.bool,
    isCollected: PropTypes.bool,
    isHosted: PropTypes.bool,
    isInShuffle: PropTypes.bool,
    isLarge: PropTypes.bool,
    isMobileWeb: PropTypes.bool,
    isPaused: PropTypes.bool,
    isPlaying: PropTypes.bool,
    isPlayingCurrently: PropTypes.bool,
    isShared: PropTypes.bool,
    lastPlayed: PropTypes.string,
    logWebViewModeEvent: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onSourceCardToggle: PropTypes.func,
    pageUrl: PropTypes.string,
    pandoraId: PropTypes.string,
    pauseTrack: PropTypes.func.isRequired,
    playResumeSource: PropTypes.func.isRequired,
    position: PropTypes.number,
    removeStationFromShuffleList: PropTypes.func.isRequired,
    routeToStationDetailsFromFactoryId: PropTypes.func.isRequired,
    shareableUrlPath: PropTypes.string,
    shouldResumePlayback: PropTypes.bool,
    showLastPlayed: PropTypes.bool,
    showRecentlyCollected: PropTypes.bool,
    sourceCardMenuItems: PropTypes.array,
    startStation: PropTypes.func.isRequired,
    stationName: PropTypes.string,
    styleNames: PropTypes.array,
    totalStations: PropTypes.number,
    undoHover: PropTypes.bool,
  };

  static contextTypes = {
    router: PropTypes.object,
  };

  static defaultProps = {
    canShuffle: false,
    createdBy: null,
    editStationUrl: null,
    isActiveShuffle: false,
    isAdvertiserStation: false,
    isInShuffle: false,
    isShared: false,
    lastPlayed: null,
    pageUrl: '',
    shouldResumePlayback: false,
  };

  startOrResumeStation = () => {
    if (this.props.shouldResumePlayback) {
      this.props.playResumeSource({
        id: this.props.id,
        pandoraType: Constants.SourceTypes.PandoraTypes.USER_STATION,
      });
    } else {
      this.props.startStation({
        componentName: 'MyStationsGridItem',
        pageUrl: this.props.pageUrl,
        pandoraId: this.props.pandoraId,
        rerouteBasedOnTier: true,
        stationId: this.props.id,
        stationListLength: this.props.totalStations,
        stationPosition: this.props.position,
      });
    }
  };

  onFirstLineClick = e => {
    e.preventDefault();

    if (this.props.editStationUrl) {
      this.context.router.push(this.props.editStationUrl);
    } else if (this.props.shareableUrlPath) {
      this.context.router.push(this.props.shareableUrlPath);
    } else {
      this.props.routeToStationDetailsFromFactoryId({
        pandoraId: this.props.pandoraId,
      });
    }
  };

  shuffleOnclickIfDisabled = () => {
    this.props.addStationToShuffleList(this.props.id);
  };

  shuffleOnclick = () => {
    this.props.removeStationFromShuffleList(this.props.id);
  };

  onMouseEnter = () => {
    if (this.props.onMouseEnter) {
      this.props.onMouseEnter();
    }

    if (this.eventActivityReported) {
      /* report interaction once */
      return;
    }
    this.eventActivityReported = true;
    const pageView = 'my_stations';
    const viewMode = 'hover_my_stations_grid';
    this.props.logWebViewModeEvent(pageView, viewMode);
  };

  onFocus = () => {
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  onSourceCardToggleCallback = isSourceCardOpen => {
    if (this.props.onSourceCardToggle) {
      this.props.onSourceCardToggle(isSourceCardOpen);
    }
  };

  getSecondTextProps = () => {
    let secondText = this.props.intl.formatMessage(messages.station);
    let secondTextModifier = '';
    let secondTextUrl = null;
    if (this.props.curatorName && this.props.curatorUrlPath) {
      secondTextModifier = this.props.intl.formatMessage(messages.stationBy);
      secondText = this.props.curatorName;
      secondTextUrl = this.props.curatorUrlPath;
    } else if (this.props.isAdvertiserStation) {
      secondText = this.props.intl.formatMessage(messages.stationByPandora);
    } else if (this.props.isShared && this.props.createdBy) {
      secondTextModifier = this.props.intl.formatMessage(messages.stationBy);
      secondText = this.props.createdBy;
      secondTextUrl = getProfilePath(this.props.createdBy);
    }

    return {
      secondText,
      secondTextModifier,
      secondTextUrl,
    };
  };

  render() {
    if (this.props.blankItem) {
      return <GridItem {...this.props} isPlaceholder={true} />;
    }

    const mainTextModifier = this.props.isHosted ? voiceTrackIcon : null;
    const { secondText, secondTextModifier, secondTextUrl } = this.getSecondTextProps();

    let thirdText = null;
    if (this.props.showLastPlayed) {
      const lastPlayed = this.props.lastPlayed && new Date(this.props.lastPlayed);
      thirdText = <LastUpdated now={new Date()} lastPlayed={lastPlayed} />;
    }

    if (this.props.showRecentlyCollected) {
      const dateCollected = this.props.dateCollected && new Date(this.props.dateCollected);
      thirdText = <LastUpdated now={new Date()} dateCollected={dateCollected} />;
    }

    return (
      <GridItem
        imgUrl={this.props.imgUrl}
        dominantColor={this.props.dominantColor}
        mainTextModifier={mainTextModifier}
        mainText={this.props.stationName}
        mainTextUrl={this.props.editStationUrl}
        mainTextOnclick={this.onFirstLineClick}
        itemOnclick={this.onFirstLineClick}
        secondTextModifier={secondTextModifier}
        secondText={secondText}
        secondTextUrl={secondTextUrl}
        thirdText={thirdText}
        isStation={!this.props.isAdvertiserStation}
        playButtonOnclick={this.startOrResumeStation}
        pauseButtonOnclick={this.props.pauseTrack}
        isPlaying={this.props.isPlaying && !this.props.isPaused}
        isPaused={this.props.isPaused}
        canShuffle={this.props.canShuffle}
        isShuffleDisabled={this.props.canShuffle && !this.props.isInShuffle}
        shuffleOnclick={this.shuffleOnclick}
        shuffleOnclickIfDisabled={this.shuffleOnclickIfDisabled}
        sourceCardMenuItems={this.props.sourceCardMenuItems}
        isLarge={this.props.isLarge}
        onMouseEnter={this.onMouseEnter}
        onFocus={this.onFocus}
        onSourceCardToggle={this.onSourceCardToggleCallback}
        isCollected={this.props.isCollected}
        isOnlyArt={this.props.isMobileWeb}
        isHosted={this.props.isHosted}
        hasTakeoverModes={this.props.hasTakeoverModes}
        {...this.props}
      />
    );
  }
}

export function mapStateToPropsWithStation(state, ownProps, station) {
  const isInShuffleMode = getInDomain('playback.utils.isInShuffleMode', false);
  const isStationIdInShuffle = getInDomain('playback.utils.isStationIdInShuffle', false);
  const isAvailableToShuffle = getInDomain('playback.utils.isAvailableToShuffle', false);
  const getStationIsAdvertiser = getInDomain('playback.utils.getStationIsAdvertiser', false);
  const isStationShuffling = getInDomain('playback.utils.isStationShuffling', false);
  const isAudioPlaying = getInDomain('playback.utils.isAudioPlaying', false);
  const getCurrentTrackTime = getInDomain('audio.utils.getCurrentTrackTime', noop);
  const isSourcePlayingCurrently = getInDomain('playback.utils.isSourcePlayingCurrently', noop);
  const getStationLastPlayed = getInDomain('playback.utils.getStationLastPlayed', noop);
  const getStationCreatedDate = getInDomain('playback.utils.getStationCreatedDate', noop);
  const hasTakeoverModes = getInDomain('playback.utils.hasTakeoverModes', false);
  const isAudioPlaying_ = isAudioPlaying(state);
  const props = {};
  const pandoraId = station.get('pandoraId');
  const stationId = station.get('stationId');
  const showModesBadge = isMegastarModesEnabled(state);
  const { curatorName, curatorUrlPath } = mapCuratorProps(
    state,
    station.get('stationFactoryPandoraId'),
  );

  const isShuffleActive = isInShuffleMode(state);
  const isInShuffle = isShuffleActive && isStationIdInShuffle(state, stationId);
  const canShuffle =
    ownProps.canShuffle && isShuffleActive && isAvailableToShuffle(state, stationId);
  const isAPSStationEnabled = isAPSPandoraStationEnabled(state);
  const sourceId = isAPSStationEnabled ? pandoraId : stationId;
  const isPlayingCurrently = isSourcePlayingCurrently(state, sourceId);

  props.dominantColor = station.get('dominantColor');
  props.imgUrl = getArt(station, GridConstants.GRID_ITEM_IMAGE_SIZE, state);
  props.stationName = getInDomain('playback.utils.getName')(station);
  props.isShared = station.get('isShared');
  props.createdBy = station.get('creatorWebname');
  props.isHosted = getInDomain('playback.utils.isHostedStation')(state, stationId);
  props.lastPlayed = ownProps.showLastPlayed ? getStationLastPlayed(station, false) : null;
  props.dateCollected = ownProps.showRecentlyCollected
    ? getStationCreatedDate(station, false)
    : null;
  props.editStationUrl = getStationDetailsPathForStationId(stationId);
  props.isAdvertiserStation = getStationIsAdvertiser(state, stationId);
  props.hasTakeoverModes = showModesBadge && hasTakeoverModes(station);
  props.isActiveShuffle = isStationShuffling(state, stationId);
  props.isInShuffle = isInShuffle;
  props.canShuffle = canShuffle;
  props.curatorName = curatorName;
  props.curatorUrlPath = curatorUrlPath;

  props.shouldResumePlayback = isPlayingCurrently && getCurrentTrackTime(state) > 0;
  props.isPlayingCurrently = isPlayingCurrently;
  props.isPlaying = isPlayingCurrently && isAudioPlaying_;
  props.isPaused = isPlayingCurrently && !isAudioPlaying_;

  props.sourceCardMenuItems = getSourceCardMenuItems({
    componentName: ownProps.componentName,
    pandoraId,
    pandoraType: PandoraTypes.STATION,
    shareParentSource: {
      shareParentSourceId: stationId,
      shareParentSourceType: PandoraTypes.STATION,
    },
    state,
    stationId,
  });

  return props;
}

export function mapStateToPropsWithoutStation(state, { pandoraId, intl }) {
  const getAnnotations = getInDomain('catalog.utils.getAnnotations');
  const isAudioPlaying = getInDomain('playback.utils.isAudioPlaying', false);
  const isStationInitialSeedPlaying = getInDomain(
    'playback.utils.isStationInitialSeedPlaying',
    false,
  );
  const getCatalogDetailsFromState = getInDomain('catalog.utils.getCatalogDetailsFromState');

  const isAudioPlaying_ = isAudioPlaying(state);
  const isPlayingCurrently = isStationInitialSeedPlaying(state, pandoraId);
  const showModesBadge = isMegastarModesEnabled(state);
  const props = {};

  const entityAnnotations = getAnnotations(state, pandoraId);
  if (entityAnnotations && !entityAnnotations.isEmpty()) {
    props.stationName = intl.formatMessage(stationGridItemMessages.radio, {
      name: entityAnnotations.get('name'),
    });
    props.hasTakeoverModes = showModesBadge && entityAnnotations.get('hasTakeoverModes');
  }

  const catalogDetails = getCatalogDetailsFromState(state, pandoraId);
  if (!catalogDetails.isEmpty()) {
    props.imgUrl = getHero(state, catalogDetails, GridConstants.GRID_ITEM_IMAGE_SIZE);
    props.dominantColor = catalogDetails.getIn(['heroImage', 'dominantColor']);
  } else if (entityAnnotations && !entityAnnotations.isEmpty()) {
    props.imgUrl = getArt(entityAnnotations, GridConstants.GRID_ITEM_IMAGE_SIZE, state);
    props.dominantColor = entityAnnotations.getIn(['heroImage', 'dominantColor']);
  }

  props.isPlayingCurrently = isPlayingCurrently;
  props.isPlaying = isPlayingCurrently && isAudioPlaying_;
  props.isPaused = isPlayingCurrently && !isAudioPlaying_;

  const { curatorName, curatorUrlPath } = mapCuratorProps(state, pandoraId);

  props.curatorName = curatorName;
  props.curatorUrlPath = curatorUrlPath;

  if (entityAnnotations.isEmpty() && catalogDetails.isEmpty()) {
    props.blankItem = true;
  }

  return props;
}

function getStation(stationId, pandoraId, state) {
  const getStationByStationId = getInDomain('playback.utils.getStationByStationId', null);
  const createGetStationByInitialSeedPandoraIdSelector = getInDomain(
    'playback.utils.createGetStationByInitialSeedPandoraIdSelector',
    noop,
  );
  let station;

  if (stationId) {
    station = getStationByStationId(state, stationId);
  }

  if (!station) {
    const getStationByInitialSeedPandoraId = createGetStationByInitialSeedPandoraIdSelector(state);
    station = getStationByInitialSeedPandoraId(pandoraId);
  }

  return station;
}

export const stationMapStateToPropsFactory = (withStation, withoutStation) => (state, ownProps) => {
  const station = ownProps.details || getStation(ownProps.id, ownProps.pandoraId, state);

  if (station) {
    return withStation(state, ownProps, station);
  }
  return withoutStation(state, ownProps);
};

export const mapStateToProps = stationMapStateToPropsFactory(
  mapStateToPropsWithStation,
  mapStateToPropsWithoutStation,
);

export const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      pauseTrack: Actions.UseCases.pauseTrack,
      playResumeSource: Actions.UseCases.playResumeSource,
      startStation: Actions.UseCases.startStation,
      addStationToShuffleList: Actions.Playback.addStationToShuffleList,
      removeStationFromShuffleList: Actions.Playback.removeStationFromShuffleList,
      routeToStationDetailsFromFactoryId: getInDomain(
        'playback.actions.routeToStationDetailsFromFactoryId',
        noop,
      ),
      logWebViewModeEvent: getInDomain('analytics.actions.logWebViewModeEvent', noop),
    },
    dispatch,
  );

const enhance = compose(
  // We use injectIntl here because there was some inexplicable performance issues when using <FormattedMessage> directly in the props
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(StationGridItem);
export { StationGridItem as PureStationGridItem };
